import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TerminalIcon, CopyIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '../ui/Button';
const codeSnippets: Record<string, string> = {
  cURL: `curl -X POST \\
  https://api.openmart.ai/api/v1/search \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "limit": 100,
    "query": "restaurants",
    "location": [{
      "city": "Los Angeles",
      "state": "CA"
    }]
  }'`,
  Python: `import requests

headers = {
    "x-api-key": "YOUR_API_KEY",
    "Content-Type": "application/json"
}

payload = {
    "limit": 100,
    "query": "restaurants",
    "location": [{
        "city": "Los Angeles",
        "state": "CA"
    }]
}

response = requests.post(
    "https://api.openmart.ai/api/v1/search",
    headers=headers,
    json=payload
)

print(response.json())`,
  JavaScript: `const response = await fetch(
  "https://api.openmart.ai/api/v1/search",
  {
    method: "POST",
    headers: {
      "x-api-key": "YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      limit: 100,
      query: "restaurants",
      location: [
        { city: "Los Angeles", state: "CA" }
      ],
    }),
  }
);

const data = await response.json();
console.log(data);`
};
const endpoints = [{
  method: 'POST',
  path: '/api/v1/search',
  description: 'Search local businesses by query and location'
}, {
  method: 'POST',
  path: '/api/v1/business_records/list/google_place_id',
  description: 'Get detailed business info via Google Place ID'
}, {
  method: 'POST',
  path: '/api/v1/business_records/list/openmart_id',
  description: 'Find business data with Openmart ID'
}, {
  method: 'POST',
  path: '/api/v1/enrich_company',
  description: 'Enrich with emails, socials & contact info'
}];
export function DeveloperAPI() {
  const [activeTab, setActiveTab] = useState('JavaScript');
  const [copied, setCopied] = useState(false);
  const tabs = ['cURL', 'Python', 'JavaScript'];
  const handleCopy = () => {
    navigator.clipboard.writeText(codeSnippets[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-2715">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-2716">
        {/* Header — left aligned */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="mb-10" data-id="element-2717">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold tracking-wide mb-5" data-id="element-2718">
            <TerminalIcon className="w-3.5 h-3.5" data-id="element-2719" />
            REST API
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-2720">
            Developer-friendly <span className="text-blue-600" data-id="element-2721">API</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-xl" data-id="element-2722">
            Integrate local business data directly into your applications with a
            few lines of code.
          </p>
        </motion.div>

        {/* Main content — unified layout */}
        <div className="grid lg:grid-cols-12 gap-0 relative z-10" data-id="element-2723">
          {/* Code Panel — dark terminal */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 bg-slate-900 rounded-2xl lg:rounded-r-none overflow-hidden" data-id="element-2724">
            {/* Terminal chrome bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50" data-id="element-2725">
              <div className="flex items-center gap-4" data-id="element-2726">
                <div className="flex items-center gap-1.5" data-id="element-2727">
                  <motion.div animate={{
                  opacity: [1, 0.2, 1],
                  scale: [1, 0.8, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="w-3 h-3 rounded-full bg-red-400" data-id="element-2728" />
                  <motion.div animate={{
                  opacity: [1, 0.2, 1],
                  scale: [1, 0.8, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.3
                }} className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-2729" />
                  <motion.div animate={{
                  opacity: [1, 0.2, 1],
                  scale: [1, 0.8, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.6
                }} className="w-3 h-3 rounded-full bg-green-400" data-id="element-2730" />
                </div>
                <div className="flex items-center gap-1 ml-2" data-id="element-2731">
                  {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === tab ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-2732">
                      {tab}
                    </button>)}
                </div>
              </div>
              <button onClick={handleCopy} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-xs" data-id="element-2733">
                {copied ? <>
                    <CheckIcon className="w-3.5 h-3.5" data-id="element-2734" />
                    Copied
                  </> : <>
                    <CopyIcon className="w-3.5 h-3.5" data-id="element-2735" />
                    Copy
                  </>}
              </button>
            </div>

            {/* Code content */}
            <div className="p-5 overflow-x-auto min-h-[360px]" data-id="element-2736">
              <AnimatePresence mode="wait" data-id="element-2737">
                <motion.pre key={activeTab} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2
              }} className="text-[13px] font-mono leading-relaxed text-slate-300" data-id="element-2738">
                  <code data-id="element-2739">{codeSnippets[activeTab]}</code>
                  <motion.span animate={{
                  opacity: [1, 0, 1]
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'steps(2)'
                }} className="inline-block w-2 h-4 bg-blue-400 ml-0.5 align-middle" data-id="element-2740" />
                </motion.pre>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right Panel — Endpoints */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="lg:col-span-5 bg-slate-50 rounded-2xl lg:rounded-l-none p-6 lg:p-8 flex flex-col border border-slate-100 lg:border-l-0" data-id="element-2741">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-5" data-id="element-2742">
              Endpoints
            </h3>

            <div className="space-y-3 flex-grow" data-id="element-2743">
              {endpoints.map((endpoint, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.3,
              delay: 0.2 + index * 0.08
            }} className="group" data-id="element-2744">
                  <div className="flex items-start gap-3 py-3 px-3 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-default" data-id="element-2745">
                    <span className="shrink-0 mt-0.5 inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-blue-600 text-white" data-id="element-2746">
                      {endpoint.method}
                    </span>
                    <div className="min-w-0" data-id="element-2747">
                      <div className="font-mono text-sm text-slate-800 truncate" data-id="element-2748">
                        {endpoint.path}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5" data-id="element-2749">
                        {endpoint.description}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-6" data-id="element-2750">
              <Button href="http://app.openmart.com/register" variant="secondary" size="md" arrow fullWidth className="!bg-slate-900 !text-white hover:!bg-slate-800 !border-slate-900" data-id="element-2751">
                View API documentation
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}